import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from "react-router-dom";
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Typography, Form, Input, Button, Divider, notification, Spin } from 'antd';
import { PasswordInput } from "antd-password-input-strength";
import _service from '@netuno/service-client';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { loggedUserInfoReloadAction } from '../../../redux/actions';

import Avatar from './Avatar';

import './index.less';

const { Title } = Typography;

function Profile({loggedUserInfo, loggedUserInfoReloadAction, history}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [avatarImageURL, setAvatarImageURL] = useState('/images/profile-default.png');
  const profileAvatar = useRef(null);
  const profileForm = useRef(null);
  const location = useLocation();

  const layout = {
    wrapperCol: { xs: { span: 24 }, sm: { span: 24 }, md: { span: 24 }, lg: { span: 12 } }
  };

  useEffect(() => {
    if (loggedUserInfo) {
      if (profileForm.current) {
        profileForm.current.setFieldsValue({
          name: loggedUserInfo?.name,
          username: loggedUserInfo?.username,
          email: loggedUserInfo?.email
        });
      }
      if (loggedUserInfo.avatar) {
        setAvatarImageURL(`${_service.config().prefix}/people/avatar?uid=${loggedUserInfo.uid}`);
      }
    }
  }, [location, loggedUserInfo]);

  function onFetchProfile() {
    setLoading(true);
    _service({
      method: 'GET',
      url: 'people',
      success: (response) => {
        setLoading(false);
        if (response.json.result) {
          profileForm.current.setFieldsValue({
            name: response.json.data.name,
            username: response.json.data.username,
            email: response.json.data.email
          });
        } else {
          notification["warning"]({
            message: 'Alerta da Matrix',
            description: response.json.error,
          });
          setLoading(false);
        }
      },
      fail: () => {
        setLoading(false);
        notification["error"]({
          message: 'Erro da Matrix',
          description: 'Ocorreu um erro ao carregar os dados, por favor tente novamente mais tarde.',
        });
      }
    });
  }

  function onFinish(values) {
    setSubmitting(true);
    const { name, username, password, email } = values;
    _service({
      method: 'PUT',
      url: 'people',
      data: {
        name,
        username,
        password,
        email,
        avatar: profileAvatar?.current?.getImage()
      },
      success: (response) => {
        if (response.json.result) {
          notification["success"]({
            message: 'Edição do Perfil',
            description: 'Os dados do seu perfil foram alterados com sucesso.',
          });
          setSubmitting(false);
          profileForm.current.setFieldsValue({
            password: "",
            password_confirm: ""
          });
          loggedUserInfoReloadAction();
        } else {
          notification["warning"]({
            message: 'Utilizador existente',
            description: response.json.error,
          });
          setSubmitting(false);
          profileForm.current.setFieldsValue({
            password: "",
            password_confirm: ""
          });
        }
      },
      fail: () => {
        setSubmitting(false);
        notification["error"]({
          message: 'Erro na Edição do Perfil',
          description: 'Ocorreu um erro na edição do seu perfil, por favor contacte-nos através do chat de suporte.',
        });
      }
    });
  }

  function onValuesChange(changedValues, allValues) {
    if (allValues.password && allValues.password.length > 0) {
      setPasswordRequired(true);
    } else {
      setPasswordRequired(false);
    }
  };

  function onFinishFailed(errorInfo) {
    console.log('Failed:', errorInfo);
  }

  if (loading) {
    return (
      <div className="loading-wrapper">
        <div className="content-title">
          <Title level={2}><Spin /> a carregar...</Title>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="content-title">
        <Button className="go-back-btn" type="link" onClick={() => history.goBack()}><ArrowLeftOutlined /> Voltar atrás</Button>
      </div>
      <div className="content-title">
        <Title level={2}>Editar Perfil</Title>
      </div>
      <div className="content-body">
        <Avatar ref={profileAvatar} currentImage={avatarImageURL}/>
        <Divider orientation="left" plain>Informações Gerais</Divider>
        <Form
          {...layout}
          onValuesChange={onValuesChange}
          ref={profileForm}
          layout="vertical"
          name="basic"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <Form.Item
            label="Nome"
            name="name"
            rules={[
              { required: true, message: 'Insira o seu nome.' },
              { type: 'string', message: 'Nome inválido, apenas letras minúsculas e maiúsculas.', pattern: "^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$" }
            ]}
          >
            <Input disabled={submitting} maxLength={25} />
          </Form.Item>
          <Form.Item
            label="Nome de utilizador"
            name="username"
            rules={[
              { required: true, message: 'Insira o seu nome.' },
              { type: 'string', message: 'Nome inválido, apenas letras minúsculas e maiúsculas.', pattern: "^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$" }
            ]}
          >
            <Input disabled={submitting} maxLength={25} />
          </Form.Item>
          <Form.Item
            label="E-mail"
            name="email"
            rules={[
              { type: 'email', message: 'O e-mail inserido não é válido.' },
              { required: true, message: 'Insira o e-mail.' }
            ]}
          >
            <Input disabled={submitting} maxLength={250} />
          </Form.Item>
          <Form.Item
            label="Nova Palavra-passe"
            name="password"
            rules={[
              { type: 'string', message: 'Palavra-Passe deverá ter entre 8 a 25 caracteres.', min: 8, max: 25 },
            ]}
          >
            <PasswordInput />
          </Form.Item>
          <Form.Item
            label="Confirmar nova Palavra-passe"
            name="password_confirm"
            rules={[
              { required: passwordRequired, message: 'Insira a confirmação da nova palavra-passe.' },
              { type: 'string', message: 'Palavra-Passe deverá ter entre 8 a 25 caracteres.', min: 8, max: 25 },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject('As palavras-passes não são iguais.');
                },
              })
            ]}
          >
            <Input.Password maxLength={25} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Atualizar Perfil
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}

const mapStateToProps = store => {
  const { loggedUserInfo } = store.loggedUserInfoState;
  return {
    loggedUserInfo
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  loggedUserInfoReloadAction
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
